import { Image } from '@rsa-digital/evo-shared-components/commonTypes/image';
import { generateKeyForElement } from '@rsa-digital/evo-shared-components/helpers/elementKeyHelper';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { Helmet } from 'react-helmet-async';

export type MetaTags = {
  title: string;
  robots?: string;
  canonical_tag?: string;
  description?: string;
  og_url?: string;
  og_title?: string;
  og_description?: string;
  og_image?: Image;
  og_type?: string;
  twitter_site?: string;
  twitter_card?: string;
  localeInfo?: Locale[];
};

export type Locale = {
  localeCode: string;
  canonicalUrl?: string;
};

type SiteWideSeo = {
  csSiteWideSeo: {
    title: string;
    favicon: { url: string };
    default_og_image: { url: string };
    default_language_code: string;
  };
};

type Site = {
  site: { siteMetadata: { siteUrl: string } };
};

const siteWideSeoQuery = graphql`
  query {
    csSiteWideSeo {
      title
      favicon {
        url
      }
      default_og_image {
        url
      }
      default_language_code
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;

export type PageMetadataProps = { meta: MetaTags };

const PageMetadata: React.FC<PageMetadataProps> = ({ meta }) => {
  const data = useStaticQuery<SiteWideSeo & Site>(siteWideSeoQuery);
  const twitterSite = meta.twitter_site || data.site.siteMetadata.siteUrl;

  const absoluteOgUrl = meta.og_url?.startsWith('/')
    ? data.site.siteMetadata.siteUrl + meta.og_url
    : meta.og_url;

  const ogImageUrlFromMetadata = meta.og_image?.url?.startsWith('/')
    ? data.site.siteMetadata.siteUrl + meta.og_image.url
    : meta.og_image?.url;

  const absoluteOgImageUrl =
    ogImageUrlFromMetadata ?? data.csSiteWideSeo.default_og_image?.url;

  const showLanguageMetadata = meta.localeInfo && meta.localeInfo.length > 1;

  const defaultLocaleCode = data.csSiteWideSeo.default_language_code;
  const defaultLocale = meta.localeInfo?.find(
    (locale) => locale.localeCode === defaultLocaleCode
  );

  // we pull the language part out of the <language>-<region> code set in the CMS
  const defaultLocaleWithoutRegion = defaultLocale?.localeCode?.substring(0, 2) || 'en';

  return (
    <Helmet
      htmlAttributes={{
        lang: defaultLocaleWithoutRegion,
      }}>
      <title>
        {meta.title} {data.csSiteWideSeo.title}
      </title>
      <link rel="icon" type="image/png" href={data.csSiteWideSeo.favicon.url} />
      {meta.robots && !meta.robots.includes('noindex') && meta.canonical_tag && (
        <link rel="canonical" href={meta.canonical_tag} />
      )}
      {meta.robots && <meta name="robots" content={meta.robots} />}
      {meta.description && <meta name="description" content={meta.description} />}
      {absoluteOgUrl && <meta property="og:url" content={absoluteOgUrl} />}
      {meta.og_title && (
        <meta
          property="og:title"
          content={`${meta.og_title}${!data.csSiteWideSeo.title ? '' : ` ${data.csSiteWideSeo.title}`}`}
        />
      )}
      {meta.og_description && (
        <meta property="og:description" content={meta.og_description} />
      )}
      {absoluteOgImageUrl && <meta property="og:image" content={absoluteOgImageUrl} />}
      {meta.og_type && <meta property="og:type" content={meta.og_type} />}
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      {meta.twitter_card && <meta name="twitter:card" content={meta.twitter_card} />}
      {showLanguageMetadata &&
        meta.localeInfo?.map(
          (locale) =>
            locale.canonicalUrl && (
              <link
                key={generateKeyForElement(locale)}
                rel="alternate"
                hrefLang={locale.localeCode}
                href={locale.canonicalUrl}
              />
            )
        )}
      {showLanguageMetadata && defaultLocale?.canonicalUrl && (
        <link hrefLang="x-default" href={defaultLocale.canonicalUrl} />
      )}
    </Helmet>
  );
};

export default PageMetadata;
